import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "../style/styles.css";
import "../style/ProductInquiry.css";
import "../style/ProductInquiryCaptcha.css";

const EMPTY_ENQUIRY = {
  product: "",
  product_category: "",
  product_subtitle: "",
  name: "",
  email: "",
  mobile: "",
  company: "",
  address: "",
  subject: "",
  message: "",
  website: "",
  captchaAnswer: "",
};

const API_ENDPOINT = "/api/send-product-enquiry.php";
const CAPTCHA_API = "/api/product-captcha.php";

const ProductInquiry = () => {
  const location = useLocation();

  const productParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      product: params.get("product") || "",
      category: params.get("category") || "",
      subtitle: params.get("subtitle") || "",
    };
  }, [location.search]);

  const [isSending, setIsSending] = useState(false);
  const [enquiryData, setEnquiryData] = useState(EMPTY_ENQUIRY);
  const [enquiryAlert, setEnquiryAlert] = useState({
    type: "",
    message: "",
  });

  const [captcha, setCaptcha] = useState({
    question: "",
    token: "",
    loading: false,
    error: "",
  });

  const loadCaptcha = useCallback(async () => {
    setCaptcha((previous) => ({
      ...previous,
      loading: true,
      error: "",
    }));

    try {
      const response = await fetch(CAPTCHA_API, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        cache: "no-store",
        credentials: "same-origin",
      });

      const contentType = response.headers.get("content-type") || "";
      const responseText = await response.text();
      const trimmedText = responseText.trim();

      if (!contentType.includes("application/json") && !trimmedText.startsWith("{")) {
        throw new Error(
          "Captcha endpoint returned HTML instead of JSON. Please check /api/product-captcha.php and .htaccess."
        );
      }

      const result = JSON.parse(trimmedText);

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load captcha.");
      }

      setCaptcha({
        question: result.question || "",
        token: result.token || "",
        loading: false,
        error: "",
      });

      setEnquiryData((previous) => ({
        ...previous,
        captchaAnswer: "",
      }));
    } catch (error) {
      setCaptcha({
        question: "",
        token: "",
        loading: false,
        error:
          error.message ||
          "Captcha loading failed. Please refresh the page and try again.",
      });
    }
  }, []);

  useEffect(() => {
    const productName = productParams.product;

    setEnquiryData({
      ...EMPTY_ENQUIRY,
      product: productName,
      product_category: productParams.category,
      product_subtitle: productParams.subtitle,
      subject: productName ? `Product Enquiry - ${productName}` : "Product Enquiry",
      message: productName
        ? `I am interested in ${productName}. Please send demo/pricing/customization details.`
        : "",
    });
  }, [productParams]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleEnquiryChange = (event) => {
    const { name, value } = event.target;

    setEnquiryData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (enquiryAlert.type !== "") {
      setEnquiryAlert({ type: "", message: "" });
    }
  };

  const validateEnquiryForm = () => {
    const product = enquiryData.product.trim();
    const name = enquiryData.name.trim();
    const email = enquiryData.email.trim();
    const mobile = enquiryData.mobile.trim();
    const message = enquiryData.message.trim();
    const captchaAnswer = enquiryData.captchaAnswer.trim();

    if (!product || !name || !email || !mobile || !message) {
      const msg = "Please enter Product, Name, Email, Mobile No. and Requirement.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Required fields missing",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      const msg = "Please enter a valid email address.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Invalid email address",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    const mobilePattern = /^[0-9+\-\s()]{7,20}$/;

    if (!mobilePattern.test(mobile)) {
      const msg = "Please enter a valid mobile number.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Invalid mobile number",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captcha.token || !captcha.question) {
      const msg = "Human verification is not loaded. Please refresh captcha and try again.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Human verification missing",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!captchaAnswer) {
      const msg = "Please answer the human verification question.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Human verification required",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!/^\d+$/.test(captchaAnswer)) {
      const msg = "Captcha answer should contain numbers only.";
      setEnquiryAlert({ type: "warning", message: msg });

      Swal.fire({
        icon: "warning",
        title: "Invalid captcha answer",
        text: msg,
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  const parseServerResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";
    const responseText = await response.text();

    if (contentType.includes("application/json")) {
      try {
        return JSON.parse(responseText);
      } catch (error) {
        return {
          success: false,
          message: "Server returned invalid JSON. Please check PHP syntax or hosting error log.",
          debug: responseText.slice(0, 300),
        };
      }
    }

    const trimmedText = responseText.trim();

    if (trimmedText.startsWith("{") || trimmedText.startsWith("[")) {
      try {
        return JSON.parse(trimmedText);
      } catch (error) {
        // continue to HTML/text error handling
      }
    }

    if (response.status === 404) {
      return {
        success: false,
        message:
          "PHP endpoint not found. Please upload send-product-enquiry.php to public/api/ and rebuild/upload.",
        debug: trimmedText.slice(0, 300),
      };
    }

    if (trimmedText.toLowerCase().includes("<html")) {
      return {
        success: false,
        message:
          "Server returned an HTML page instead of JSON. This usually means the PHP file is missing, blocked, or there is a hosting/PHP error.",
        debug: trimmedText.slice(0, 300),
      };
    }

    return {
      success: false,
      message:
        trimmedText ||
        "Invalid server response. Please check /api/send-product-enquiry.php directly in browser.",
      debug: trimmedText.slice(0, 300),
    };
  };

  const submitEnquiryForm = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    if (!validateEnquiryForm()) {
      return;
    }

    setIsSending(true);
    setEnquiryAlert({
      type: "info",
      message: "Sending product enquiry. Please wait...",
    });

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-store",
        credentials: "same-origin",
        body: JSON.stringify({
          product: enquiryData.product.trim(),
          product_category: enquiryData.product_category.trim(),
          product_subtitle: enquiryData.product_subtitle.trim(),
          name: enquiryData.name.trim(),
          email: enquiryData.email.trim(),
          mobile: enquiryData.mobile.trim(),
          company: enquiryData.company.trim(),
          address: enquiryData.address.trim(),
          subject:
            enquiryData.subject.trim() ||
            `Product Enquiry - ${enquiryData.product.trim()}`,
          message: enquiryData.message.trim(),
          website: enquiryData.website.trim(),
          captcha_token: captcha.token,
          captcha_answer: enquiryData.captchaAnswer.trim(),
        }),
      });

      const result = await parseServerResponse(response);

      if (!response.ok || !result.success) {
        if (
          (result.message || "").toLowerCase().includes("captcha") ||
          (result.message || "").toLowerCase().includes("verification")
        ) {
          loadCaptcha();
        }

        throw new Error(result.message || "Product enquiry sending failed.");
      }

      const successMessage =
        "Product enquiry sent successfully to info@ebssoftware.co.in and ebs.slg@gmail.com.";

      setEnquiryAlert({
        type: "success",
        message: successMessage,
      });

      await Swal.fire({
        icon: "success",
        title: "Product Enquiry Sent",
        text: successMessage,
        confirmButtonText: "OK",
      });

      loadCaptcha();

      setEnquiryData({
        ...EMPTY_ENQUIRY,
        product: enquiryData.product,
        product_category: enquiryData.product_category,
        product_subtitle: enquiryData.product_subtitle,
        subject: enquiryData.product
          ? `Product Enquiry - ${enquiryData.product}`
          : "Product Enquiry",
        message: enquiryData.product
          ? `I am interested in ${enquiryData.product}. Please send demo/pricing/customization details.`
          : "",
      });
    } catch (error) {
      console.error("Product enquiry send error:", error);

      const errorMessage =
        error.message ||
        "Unable to send your product enquiry right now. Please email us directly at info@ebssoftware.co.in and ebs.slg@gmail.com.";

      setEnquiryAlert({
        type: "error",
        message: errorMessage,
      });

      Swal.fire({
        icon: "error",
        title: "Email Sending Failed",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getAlertClassName = () => {
    if (enquiryAlert.type === "success") {
      return "product-enquiry-inline-alert product-enquiry-inline-alert-success";
    }

    if (enquiryAlert.type === "error") {
      return "product-enquiry-inline-alert product-enquiry-inline-alert-error";
    }

    if (enquiryAlert.type === "warning") {
      return "product-enquiry-inline-alert product-enquiry-inline-alert-warning";
    }

    return "product-enquiry-inline-alert product-enquiry-inline-alert-info";
  };

  return (
    <main className="product-enquiry-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Product Enquiry | EBS Software Solution</title>
        <meta
          name="description"
          content="Send product enquiry to EBS Software Solution for software demo, pricing, customization and implementation support."
        />
      </Helmet>

      <section className="product-enquiry-hero">
        <div className="product-enquiry-hero-overlay"></div>

        <Container className="product-enquiry-hero-container">
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <div className="product-enquiry-hero-content">
                <div className="product-enquiry-kicker">
                  EBS Software Product Enquiry
                </div>
                <h1>Product Enquiry Form</h1>
                <p>
                  Send your product enquiry for demo, pricing, customization,
                  implementation or support. Your enquiry will be sent to
                  info@ebssoftware.co.in and ebs.slg@gmail.com.
                </p>
              </div>
            </Col>

            <Col lg={4}>
              <div className="product-enquiry-hero-card">
                <span>Selected Product</span>
                <strong>{enquiryData.product || "Product Enquiry"}</strong>
                <p>{enquiryData.product_subtitle || "Please fill the enquiry form below."}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="product-enquiry-main-section">
        <Container>
          <Row className="g-4 align-items-stretch">
            <Col lg={8}>
              <div className="product-enquiry-form-card">
                <div className="product-enquiry-section-label">
                  Send Enquiry
                </div>
                <h2>Enquiry Details</h2>
                <p className="product-enquiry-form-intro">
                  Please fill all required fields. The selected product is already
                  filled from the Product page.
                </p>

                {enquiryAlert.message !== "" && (
                  <div className={getAlertClassName()}>{enquiryAlert.message}</div>
                )}

                {captcha.error !== "" && (
                  <div className="product-enquiry-inline-alert product-enquiry-inline-alert-error">
                    {captcha.error}
                  </div>
                )}

                <form onSubmit={submitEnquiryForm} noValidate>
                  <input
                    type="text"
                    name="website"
                    value={enquiryData.website}
                    onChange={handleEnquiryChange}
                    tabIndex="-1"
                    autoComplete="off"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      height: 0,
                      width: 0,
                      opacity: 0,
                    }}
                    aria-hidden="true"
                  />

                  <Row className="g-3">
                    <Col md={12}>
                      <label htmlFor="enquiry_product">Product *</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_product"
                        name="product"
                        type="text"
                        value={enquiryData.product}
                        onChange={handleEnquiryChange}
                        placeholder="Enter product name"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="enquiry_name">Name *</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_name"
                        name="name"
                        type="text"
                        value={enquiryData.name}
                        onChange={handleEnquiryChange}
                        placeholder="Enter your name"
                        autoComplete="name"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="enquiry_email">Email *</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_email"
                        name="email"
                        type="email"
                        value={enquiryData.email}
                        onChange={handleEnquiryChange}
                        placeholder="Enter your email"
                        autoComplete="email"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="enquiry_mobile">Mobile No. *</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_mobile"
                        name="mobile"
                        type="tel"
                        value={enquiryData.mobile}
                        onChange={handleEnquiryChange}
                        placeholder="Enter mobile number"
                        autoComplete="tel"
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <label htmlFor="enquiry_company">Company / Business Name</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_company"
                        name="company"
                        type="text"
                        value={enquiryData.company}
                        onChange={handleEnquiryChange}
                        placeholder="Enter company name"
                      />
                    </Col>

                    <Col md={12}>
                      <label htmlFor="enquiry_address">Address</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_address"
                        name="address"
                        type="text"
                        value={enquiryData.address}
                        onChange={handleEnquiryChange}
                        placeholder="Enter address"
                        autoComplete="street-address"
                      />
                    </Col>

                    <Col md={12}>
                      <label htmlFor="enquiry_subject">Subject</label>
                      <input
                        className="product-enquiry-input"
                        id="enquiry_subject"
                        name="subject"
                        type="text"
                        value={enquiryData.subject}
                        onChange={handleEnquiryChange}
                        placeholder="Product enquiry subject"
                      />
                    </Col>

                    <Col md={12}>
                      <label htmlFor="enquiry_message">Requirement / Message *</label>
                      <textarea
                        className="product-enquiry-input product-enquiry-textarea"
                        id="enquiry_message"
                        name="message"
                        value={enquiryData.message}
                        onChange={handleEnquiryChange}
                        placeholder="Write your product requirement, demo request, customization details or questions"
                        rows="5"
                        required
                      />
                    </Col>

                    <Col md={12}>
                      <div className="product-enquiry-captcha-box">
                        <div className="product-enquiry-captcha-question-wrap">
                          <div>
                            <label htmlFor="captchaAnswer">
                              Human Verification *
                            </label>
                            <div className="product-enquiry-captcha-question">
                              {captcha.loading
                                ? "Loading captcha..."
                                : captcha.question || "Captcha not loaded"}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="product-enquiry-captcha-refresh"
                            onClick={loadCaptcha}
                            disabled={captcha.loading || isSending}
                          >
                            Refresh
                          </button>
                        </div>

                        <input
                          className="product-enquiry-input"
                          type="text"
                          id="captchaAnswer"
                          name="captchaAnswer"
                          value={enquiryData.captchaAnswer}
                          onChange={handleEnquiryChange}
                          placeholder="Enter answer"
                          inputMode="numeric"
                          autoComplete="off"
                          required
                        />
                      </div>
                    </Col>

                    <Col md={12}>
                      <button
                        type="submit"
                        className="product-enquiry-submit-btn"
                        disabled={isSending || captcha.loading || !captcha.token}
                      >
                        {isSending ? "Sending Enquiry..." : "Send Product Enquiry"}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>

            <Col lg={4}>
              <div className="product-enquiry-side-card">
                <div className="product-enquiry-section-label">
                  Email Destination
                </div>
                <h2>Send To</h2>

                <div className="product-enquiry-destination-card">
                  <span>Primary Email</span>
                  <strong>info@ebssoftware.co.in</strong>
                </div>

                <div className="product-enquiry-destination-card">
                  <span>Second Email</span>
                  <strong>ebs.slg@gmail.com</strong>
                </div>

                <div className="product-enquiry-destination-card">
                  <span>Phone</span>
                  <strong>+91 97337 21111 / 99320 13501</strong>
                </div>

                <p>
                  After successful submission, you will receive a clear alert on
                  this page and a popup confirmation.
                </p>

                <Link to="/Product" className="product-enquiry-back-link">
                  ← Back to Products
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default ProductInquiry;
